/**
 * پیشنهادِ رخداد/بیزینسِ تازه — قرینه‌ی apps/mobile/app/suggest.tsx.
 *
 * پیشنهاد با وضعیتِ «در انتظار بررسی» ثبت می‌شود و تا تاییدِ ادمین دیده
 * نمی‌شود. لینکِ منبع اجباری است — بدونِ آن پیشنهاد راستی‌آزمایی‌پذیر نیست.
 *
 * **محدودِ به یک تاریخ**، برخلافِ فرمِ موبایل که الگوهای تکرار (هر پنجشنبه،
 * روزِ ۱ هر ماه) و چند تاریخِ پراکنده را هم می‌پذیرد. آن گزینه‌گرِ تاریخِ
 * پیشرفته (`EventDatePicker`) پیچیدگیِ زیادی برای این برش دارد؛ رخدادِ
 * تکرارشونده هنوز فقط از موبایل قابلِ‌پیشنهاد است — نبودش اینجا عمدی و
 * ثبت‌شده است، نه فراموش‌شده.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { businessRepo, eventRepo, tagRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { useCityScope } from '@/core/lib/cityScope';
import { t, useLocale } from '@/core/lib/i18n';
import { looksLikeUrl } from '@/core/lib/url';
import { toDraft, type EventDates } from '@/core/lib/eventDates';
import { reportError } from '@/core/lib/report';
import { Card, Checkbox, Help, Note, Select } from '@/ui/kit';
import { TagPicker } from '@/ui/TagPicker';
import { pickWideImage } from '@/features/profile/imagePicker';
import { LocationPicker } from '@/features/discover/LocationPicker';
import { DESCRIBE_KEYS } from '@/core/lib/myEvents';
import { usablePoint } from '@/core/lib/mapPicker';
import type { SuggestionDraft } from '@/core/domain/types';

const empty: SuggestionDraft = {
  kind: 'business',
  name: '', url: '', description: '', address: '',
  tagIds: [], startsAt: undefined, endsAt: undefined,
  isFree: false, hasDiscount: false, discountNote: '',
  ticketUrl: '', imageUrl: '', isOrganizer: false,
};

export function SuggestPage() {
  useLocale();
  const { profile } = useSession();
  const city = useCityScope();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [d, setD] = useState<SuggestionDraft>(empty);
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('19:00');
  const [endTime, setEndTime] = useState('21:00');
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });
  const set = <K extends keyof SuggestionDraft>(k: K, v: SuggestionDraft[K]) =>
    setD((x) => ({ ...x, [k]: v }));

  /**
   * انتخاب و آپلودِ عکس در یک حرکت — قرینه‌ی فرمِ موبایل.
   *
   * آپلود همین‌جا انجام می‌شود نه هنگامِ ذخیره: کاربر باید قبل از فرستادن
   * ببیند چه انتخاب کرده، و شکستِ آپلود باید همان‌جا معلوم شود نه بعد از
   * پر کردنِ کلِ فرم.
   */
  const pickImage = async () => {
    setPicking(true);
    try {
      const picked = await pickWideImage();
      if (picked.kind !== 'ok') {
        if (picked.kind === 'error') setError(t('suggestImageFailed'));
        return;
      }
      const url = await eventRepo.uploadImage(profile!.id, picked.base64);
      if (d.imageUrl) eventRepo.removeImage(d.imageUrl).catch(() => undefined);
      set('imageUrl', url);
      setError(null);
    } catch (e) {
      reportError('web.suggest.image', e);
      setError(t('suggestImageFailed'));
    } finally {
      setPicking(false);
    }
  };

  const eventDates = (): EventDates => {
    if (!date) return { dates: [], weekdays: [], monthdays: [], monthWeeks: [] };
    const [y, m, dd] = date.split('-').map(Number);
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    return {
      dates: [{
        start: new Date(y, m - 1, dd), end: null,
        startTime: { h: sh || 0, m: sm || 0 }, endTime: { h: eh || 0, m: em || 0 },
      }],
      weekdays: [], monthdays: [], monthWeeks: [],
    };
  };

  /**
   * پیشنهاد برای شهری ثبت می‌شه که کاربر همین الان داره می‌بینه — نه یه شهرِ
   * ثابت. اگه شهرِ موقت روی فهرست نشسته باشه، پیشنهاد هم مالِ همونه؛ کسی که
   * فهرستِ تورنتو رو باز کرده و یه جای جامانده رو می‌بینه، دارهٔ تورنتو رو
   * کامل می‌کنه نه شهرِ خودش رو.
   */
  const save = useMutation({
    mutationFn: () => (d.kind === 'business'
      ? businessRepo.suggest(profile!.id, city!.id, d)
      : eventRepo.suggest(profile!.id, city!.id, { ...d, ...toDraft(eventDates()) })),
    onSuccess: () => {
      // پینِ بالای صفحه‌ی کشف باید همین الان نشانش دهد — «بلافاصله» بخشی از
      // خودِ خواسته بود، نه بعد از یک رفرش.
      qc.invalidateQueries({ queryKey: ['myEvents'] });
      nav('/');
    },
    onError: (e) => { reportError('web.suggest.save', e); setError(t('errorRetry')); },
  });

  const urlOk = looksLikeUrl(d.url);
  /**
   * رخداد بدونِ تاریخ ثبت نمی‌شود: کارتش تا **تاریخِ پایان** بالای فهرستِ
   * خودش پین می‌ماند (مایگریشن 0077)، و پایان از همین یک تاریخ و ساعتِ
   * پایان ساخته می‌شود.
   */
  const endOk = d.kind === 'business' || !!date;
  const canSave = d.name.trim().length > 0 && urlOk && endOk && !!city && !save.isPending;

  return (
    <div className="feed">
      <h1 className="feed-title">{t('suggestTitle')}</h1>

      <Card>
        <h3>{t('suggestWhat')}</h3>
        <Select value={d.kind}
          items={[
            { value: 'business', label: t('suggestBusiness'), hint: t('suggestBusinessHint') },
            { value: 'event', label: t('suggestEvent'), hint: t('suggestEventHint') },
          ]}
          onChange={(v) => set('kind', v)} />
      </Card>

      <Card>
        <h3>{t('suggestDetails')}</h3>
        <input className="input" value={d.name} onChange={(e) => set('name', e.target.value)}
          placeholder={d.kind === 'business' ? t('suggestNameBizPh') : t('suggestNameEventPh')} />

        {/* عکس **بلافاصله بعد از اسم**، نه ته فرم — قرینه‌ی همان جابه‌جایی در
            فرمِ موبایل. اولین کاربر پرسید «کجا باید عکس بدم؟»: قبلاً زیرِ
            تقویم بود و برای پیدا کردنش باید کلِ فرم را می‌گشتی.

            برای **مکان هم** هست، نه فقط رخداد: کارتِ یک کافه بدونِ عکس هم
            همان‌قدر بی‌جان دیده می‌شود.

            «چرا عکس» عمداً جلوی چشم است نه پشتِ «؟»: متنِ ترغیبی است و پشتِ
            آیکن کسی نمی‌بیندش که عکس بگذارد. */}
        <h3>{t('suggestImage')}</h3>
        <p className="dim">{t('suggestImageWhy')}</p>
        {d.imageUrl ? <img className="hero-img" src={d.imageUrl} alt="" /> : null}
        <div className="chiprow">
          <button type="button" className="btn" disabled={picking} onClick={pickImage}>
            {d.imageUrl ? t('suggestImageChange') : t('suggestImagePick')}
          </button>
          {d.imageUrl ? (
            <button type="button" className="btn" onClick={() => {
              eventRepo.removeImage(d.imageUrl).catch(() => undefined);
              set('imageUrl', '');
            }}>
              {t('suggestImageRemove')}
            </button>
          ) : null}
        </div>

        <label className="field">
          <span>{t('suggestUrl')}<Help text={t('suggestUrlWhy')} /></span>
          <input className="input" value={d.url} onChange={(e) => set('url', e.target.value)}
            placeholder="example.com" />
        </label>
        {d.url.length > 0 && !urlOk ? <p className="dim">{t('suggestUrlInvalid')}</p> : null}

        {/* فهرستِ «اینها را بنویس» پشتِ همان «؟» است، نه هفت خطِ زیرِ کادر —
            و فقط برای رخداد: کسی که کافه معرفی می‌کند رنجِ سنی و پارکینگ
            نمی‌نویسد. */}
        <label className="field">
          <span>
            {t('suggestDescription')}
            <Help lines={d.kind === 'event' ? DESCRIBE_KEYS.map((k) => t(k)) : undefined} />
          </span>
          <textarea className="input textarea" value={d.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder={t('suggestDescriptionPh')} rows={3} />
        </label>

        {/* کجا — با متن، و اگر خواست روی نقشه. برای **هر دو** نوع: مکان حتی
            بیشتر از رخداد به نقطه احتیاج دارد، چون یک کافه‌ی توی کوچه با
            آدرسِ تنها پیدا نمی‌شود. */}
        <LocationPicker
          address={d.address} onAddress={(v) => set('address', v)}
          point={usablePoint(d) ? { lat: d.lat!, lng: d.lng! } : null}
          onPoint={(p) => setD((x) => ({ ...x, lat: p?.lat, lng: p?.lng }))}
          center={city?.lat != null && city?.lng != null
            ? { lat: city.lat, lng: city.lng } : null} />

        {d.kind === 'business' ? null : (
          <>
            <div className="two-up">
              <label className="field">
                {/* «تاریخ پایان لازم است» هشدارِ جدا نمی‌خواهد: ساعتِ پایان
                    همیشه پر است، پس همین یک تاریخ کافی است. توضیحش پشتِ «؟». */}
                <span>{t('pickDate')}<Help text={t('suggestEndRequired')} /></span>
                <input className="input" type="date" value={date} required
                  onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className="field">
                <span>{t('when')}</span>
                <div className="two-up">
                  <input className="input" type="time" value={startTime}
                    onChange={(e) => setStartTime(e.target.value)} />
                  <input className="input" type="time" value={endTime}
                    onChange={(e) => setEndTime(e.target.value)} />
                </div>
              </label>
            </div>
            <label className="field">
              <span>{t('suggestTicketUrl')}<Help text={t('suggestTicketUrlHint')} /></span>
              <input className="input" value={d.ticketUrl}
                onChange={(e) => set('ticketUrl', e.target.value)}
                placeholder="example.com/tickets" />
            </label>

            {/* ادعای برگزارکنندگی. اثرش امروز صفر است و فردا هر ویرایش:
                نگاه کن به `save_own_event` در مایگریشن 0077. */}
            <div className="chiprow">
              <Checkbox label={t('suggestOrganizer')} checked={!!d.isOrganizer}
                onChange={(v) => set('isOrganizer', v)} />
              <Help text={d.isOrganizer ? t('suggestOrganizerHint') : t('suggestAnnouncerHint')} />
            </div>
          </>
        )}

        <h3>{t('category')}</h3>
        <TagPicker value={d.tagIds} onChange={(ids) => set('tagIds', ids)}
          tags={tags.data ?? []} />

        {/* دوحالته‌ها هم دراپ‌دان — مقدارِ Select رشته‌ست، بولی همین‌جا ترجمه می‌شه. */}
        {d.kind === 'event' ? (
          <Select value={d.isFree ? 'free' : 'paid'}
            items={[
              { value: 'paid', label: t('notFree') },
              { value: 'free', label: t('onlyFree') },
            ]}
            onChange={(v) => set('isFree', v === 'free')} />
        ) : (
          <>
            <Select value={d.hasDiscount ? 'yes' : 'no'}
              items={[
                { value: 'no', label: t('noDiscount') },
                { value: 'yes', label: t('hasDiscountQ') },
              ]}
              onChange={(v) => set('hasDiscount', v === 'yes')} />
            {d.hasDiscount ? (
              <input className="input" value={d.discountNote}
                onChange={(e) => set('discountNote', e.target.value)}
                placeholder={t('discountNotePh')} />
            ) : null}
          </>
        )}

        {error ? <p className="error">{error}</p> : null}
        <button type="button" className="btn fill block" disabled={!canSave}
          onClick={() => save.mutate()}>
          {save.isPending ? t('saving') : t('suggestSend')}
        </button>
        {!d.name.trim() ? <p className="dim">{t('nameRequired')}</p> : null}
        {d.name.trim() && !urlOk ? <p className="dim">{t('suggestUrlRequired')}</p> : null}
        {d.name.trim() && urlOk && !endOk ? <p className="dim">{t('suggestEndRequired')}</p> : null}
      </Card>

      <Note>{t('suggestReviewNote')}</Note>
    </div>
  );
}
